import { useState } from "react";
import { MENU_CATEGORIES } from "../data/menu";

export const MenuSection = () => {
  const [active, setActive] = useState(MENU_CATEGORIES[0].id);
  const current = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-[#0A0A0A] text-white py-16 sm:py-24 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-red-600" />
              <span className="text-[11px] uppercase tracking-[0.35em] text-red-500 font-semibold">
                Nuestro menú
              </span>
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.95] tracking-tighter">
              Carta <span className="text-red-600">completa</span>
            </h2>
            <p className="text-white/60 mt-3 max-w-md text-base">
              {MENU_CATEGORIES.length} categorías preparadas al momento. Precios
              referenciales; consulte disponibilidad en sucursal.
            </p>
          </div>
        </div>

        {/* Categoría chips — scroll horizontal en móvil */}
        <div
          data-testid="menu-tabs"
          className="flex gap-2 sm:gap-2.5 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide"
        >
          {MENU_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActive(cat.id)}
              data-testid={`menu-tab-${cat.id}`}
              className={`shrink-0 px-3.5 sm:px-5 py-2 sm:py-2.5 text-[11px] sm:text-sm uppercase tracking-widest border transition-all ${
                active === cat.id
                  ? "bg-red-600 border-red-600 text-white"
                  : "bg-transparent border-white/15 text-white/70 hover:text-white hover:border-white/40"
              }`}
            >
              {cat.title}
            </button>
          ))}
        </div>

        {/* Categoría activa */}
        <div
          data-testid={`menu-panel-${current.id}`}
          className="mt-10 border border-white/10 bg-black/40 p-6 sm:p-10"
        >
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-8 pb-6 border-b border-white/10">
            <h3 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white">
              {current.title}
            </h3>
            {current.note && (
              <p className="text-xs sm:text-sm text-white/55 italic max-w-md sm:text-right">
                {current.note}
              </p>
            )}
          </div>

          {current.layout === "combos" ? (
            <div
              data-testid={`menu-combos-grid`}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
            >
              {current.items.map((combo, idx) => (
                <article
                  key={`${current.id}-${idx}`}
                  data-testid={`menu-item-${current.id}-${idx}`}
                  className={`relative border ${
                    combo.featured
                      ? "border-orange-500/50 bg-orange-500/[0.04]"
                      : "border-white/10 hover:border-red-600/60 bg-black/40"
                  } p-4 sm:p-5 transition-colors group flex flex-col`}
                >
                  {combo.featured && (
                    <span className="absolute -top-2 left-4 text-[9px] uppercase tracking-[0.3em] text-black bg-orange-500 px-2 py-0.5 font-semibold">
                      Destacado
                    </span>
                  )}
                  <div className="flex items-baseline justify-between gap-3 pb-3 border-b border-white/10">
                    <h4 className="font-display uppercase text-xl sm:text-2xl text-white tracking-tight">
                      {combo.name}
                    </h4>
                    <span className="font-display text-2xl sm:text-3xl text-red-500 tracking-tight whitespace-nowrap">
                      {combo.price}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm text-white/80 flex-1">
                    {combo.details.map((d, didx) => (
                      <li key={didx} className="flex items-start gap-2">
                        <span
                          aria-hidden
                          className="mt-1.5 w-1 h-1 bg-red-500 shrink-0"
                        />
                        <span className="leading-snug">
                          {Array.isArray(d) ? (
                            d.map((opt, oi) => (
                              <span key={oi}>
                                {oi > 0 && (
                                  <span className="text-orange-400 italic mx-1.5 lowercase">
                                    ó
                                  </span>
                                )}
                                {opt}
                              </span>
                            ))
                          ) : (
                            d
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          ) : (
            <ul className="divide-y divide-white/10">
              {current.items.map((item, idx) => (
                <li
                  key={`${current.id}-${idx}`}
                  data-testid={`menu-item-${current.id}-${idx}`}
                  className="py-4 sm:py-5 group"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-base sm:text-lg font-medium leading-snug group-hover:text-red-500 transition-colors">
                        {item.name}
                      </p>
                      {item.description && (
                        <p className="text-white/55 text-sm mt-1">
                          {item.description}
                        </p>
                      )}
                    </div>
                    {item.price && (
                      <span className="font-display text-xl sm:text-2xl text-red-500 tracking-tight whitespace-nowrap pt-1">
                        {item.price}
                      </span>
                    )}
                  </div>

                  {item.sizes && (
                    <div
                      data-testid={`menu-item-${current.id}-${idx}-sizes`}
                      className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 sm:gap-x-8 gap-y-1.5 mt-3 sm:mt-4"
                    >
                      {item.sizes.map((s, sidx) => (
                        <div
                          key={sidx}
                          className="flex items-center justify-between gap-3 border-b border-white/5 pb-1"
                        >
                          <span className="text-[11px] sm:text-xs uppercase tracking-widest text-white/55">
                            {s.label}
                          </span>
                          <span className="font-display text-base sm:text-lg text-red-500 tracking-tight whitespace-nowrap">
                            {s.price}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {current.footerNote && (
            <div
              data-testid={`menu-footer-note-${current.id}`}
              className="mt-6 sm:mt-8 flex items-center gap-3 border border-orange-500/40 bg-orange-500/10 px-4 py-3 sm:px-5 sm:py-4"
            >
              <span className="w-1.5 h-6 bg-orange-500" aria-hidden />
              <p className="text-sm sm:text-base text-white font-medium">
                <span className="text-orange-400 font-display uppercase tracking-widest text-xs sm:text-sm mr-2">
                  Combo:
                </span>
                {current.footerNote}
              </p>
            </div>
          )}
        </div>

        {/* Hint */}
        <p className="text-center text-xs uppercase tracking-[0.3em] text-white/40 mt-8 pb-4">
          Seleccione una categoría para ver más
        </p>
      </div>
    </section>
  );
};
